import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Map, { Marker, Popup, NavigationControl, Source, Layer } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Calendar, Map as MapIcon, X, Camera, CheckCircle, Plus, ChevronLeft, ChevronRight, Users, Play } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import TripScrapbook from './components/TripScrapbook';
import TripReplayDemo from './components/TripReplayDemo';
import './App.css';

// User Configuration
const users = {
  user1: {
    id: 'user1',
    name: 'James',
    username: '@james',
    color: '#FFD700', // Gold/Yellow
    secondaryColor: '#FFF8DC', // Cornsilk
    avatar: null,
    friends: ['user2']
  },
  user2: {
    id: 'user2',
    name: 'Barnabas',
    username: '@barnabas',
    color: '#FF6B6B', // Red/Pink
    secondaryColor: '#FFE4E1', // MistyRose
    avatar: null,
    friends: ['user1']
  }
};

// Helper to create a GeoJSON circle
const createGeoJSONCircle = (center, radiusInKm, points = 64) => {
  const coords = {
    latitude: center[1],
    longitude: center[0]
  };

  const km = radiusInKm;

  const ret = [];
  const distanceX = km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
  const distanceY = km / 110.574;

  let theta, x, y;
  for (let i = 0; i < points; i++) {
    theta = (i / points) * (2 * Math.PI);
    x = distanceX * Math.cos(theta);
    y = distanceY * Math.sin(theta);

    ret.push([coords.longitude + x, coords.latitude + y]);
  }
  ret.push(ret[0]);

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [ret]
    },
    properties: {}
  };
};

// Sample trip data
const sampleTripsUser1 = [
  {
    id: 1,
    userId: 'user1',
    title: 'Phipps Conservatory',
    city: 'Pittsburgh, PA',
    coordinates: [-79.9459, 40.4390],
    date: 'Spring 2024',
    photos: [
      { url: '/phipps-1.jpg', caption: 'Beautiful flowers' },
      { url: '/phipps-2.jpg', caption: 'Glasshouse view' }
    ],
    pinIcon: '/pin-icon.png?v=2',
    description: "A cozy family day exploring the gardens and glasshouse at Phipps Conservatory with Kayla and our 9‑month‑old son Luke.",
    highlights: [
      'Stroller‑friendly walk through the botanical gardens',
      "Luke's first big greenhouse adventure",
      'Time together with Kayla in a calm, beautiful space'
    ]
  },
  {
    id: 2,
    userId: 'user1',
    title: 'Hawaiian Adventure',
    city: 'Maui, HI',
    coordinates: [-156.3319, 20.7984],
    date: 'Summer 2024',
    photos: [
      { url: '/hawaii-1.jpg', caption: 'Sunset at the beach' }
    ],
    pinIcon: '/h-badge.png?v=2',
    description: "An unforgettable trip to the beautiful islands of Hawaii, soaking in the sun, surf, and stunning natural beauty.",
    highlights: [
      'Beautiful beaches and crystal-clear waters',
      'Exploring volcanic landscapes',
      'Relaxing island vibes and incredible sunsets'
    ]
  },
  {
    id: 3,
    userId: 'user1',
    title: 'YSU Trip',
    city: 'Youngstown, OH',
    coordinates: [-80.6498, 41.1067],
    date: 'Spring 2024',
    photos: [],
    pinIcon: '/ysu-trip-badge.png',
    transparentBg: true,
    isReplayTrip: true,
    description: "A road trip from Cleveland down to Youngstown State University and back. Diner stop, campus walk, coffee, and one unexpected flat tire.",
    highlights: [
      'Cleveland to Streetsboro diner pit stop',
      'Walking the YSU campus on a clear day',
      'Cozy Bean Coffee — 5★ espresso',
      'Picked up a flat tire on the way home (no big deal)'
    ]
  }
];

const sampleTripsUser2 = [
  {
    id: 101,
    userId: 'user2',
    title: 'Tokyo Exploration',
    city: 'Tokyo, Japan',
    coordinates: [139.6917, 35.6895],
    date: 'Autumn 2023',
    photos: [],
    pinIcon: null,
    icon: '🗼',
    description: "Exploring the bustling streets of Tokyo, from Shibuya Crossing to the serene Meiji Shrine.",
    highlights: [
      'Sushi at Tsukiji Outer Market',
      'Shibuya Crossing chaos',
      'Peaceful walk in Yoyogi Park'
    ]
  },
  {
    id: 102,
    userId: 'user2',
    title: 'Paris Getaway',
    city: 'Paris, France',
    coordinates: [2.3522, 48.8566],
    date: 'Spring 2023',
    photos: [],
    pinIcon: null,
    icon: '🥐',
    description: "A romantic weekend in Paris, enjoying croissants, art, and the Eiffel Tower.",
    highlights: [
      'Eiffel Tower at night',
      'Louvre Museum',
      'Croissants every morning'
    ]
  }
];

const initialTrips = [...sampleTripsUser1, ...sampleTripsUser2];

// Runtime background-stripper: turns near-white pixels of an image into
// transparency so badges with a white background look like proper stickers
// on the map. Returns an object map of { [src]: dataUrl } once processed.
function useTransparentImages(srcs) {
  const [map, setMap] = useState({});
  useEffect(() => {
    srcs.forEach(src => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const px = data.data;
          const T = 235;
          for (let i = 0; i < px.length; i += 4) {
            const r = px[i], g = px[i + 1], b = px[i + 2];
            if (r >= T && g >= T && b >= T) {
              px[i + 3] = 0;
            } else {
              const m = Math.min(r, g, b);
              if (m > 200) {
                const f = (m - 200) / (T - 200);
                px[i + 3] = Math.round(px[i + 3] * (1 - f));
              }
            }
          }
          ctx.putImageData(data, 0, 0);
          setMap(prev => ({ ...prev, [src]: canvas.toDataURL('image/png') }));
        } catch (e) { /* fall back to raw src */ }
      };
      img.src = src;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return map;
}

function Home() {
  const navigate = useNavigate();
  const [allTrips, setAllTrips] = useState(initialTrips);
  const [viewingUserId, setViewingUserId] = useState('user1');
  const [activeTab, setActiveTab] = useState('trips'); // 'trips' | 'friends'

  // Pre-process any trip badges flagged as needing background removal so the
  // pin renders as a clean sticker on the map.
  const transparentBadgeSrcs = useMemo(
    () => Array.from(new Set(
      allTrips.filter(t => t.transparentBg && t.pinIcon).map(t => t.pinIcon)
    )),
    [allTrips]
  );
  const transparentBadges = useTransparentImages(transparentBadgeSrcs);
  
  // Filter trips for the current view
  const trips = allTrips.filter(t => t.userId === viewingUserId);
  
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showScrapbook, setShowScrapbook] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [viewState, setViewState] = useState({
    longitude: -98.5795,
    latitude: 39.8283,
    zoom: 3.5
  });

  const resetZoom = () => {
    setViewState({
      longitude: -98.5795,
      latitude: 39.8283,
      zoom: 3.5,
      transitionDuration: 1000
    });
    setSelectedTrip(null);
  };

  const handleMapClick = (e) => {
    if (!isEditMode) return;

    // Create a new empty trip at this location
    const newTrip = {
      id: Date.now(),
      userId: viewingUserId, // Create for current user
      title: 'New Adventure',
      city: 'Unknown Location',
      coordinates: [e.lngLat.lng, e.lngLat.lat],
      date: 'Today',
      photos: [],
      icon: '📍',
      description: "Describe your adventure here...",
      highlights: []
    };

    setAllTrips([...allTrips, newTrip]);
    setSelectedTrip(newTrip);
    setShowScrapbook(true);
  };

  const handlePinClick = (trip) => {
    setSelectedTrip(trip);
    setViewState({
      ...viewState,
      longitude: trip.coordinates[0],
      latitude: trip.coordinates[1],
      zoom: 12,
      transitionDuration: 1000
    });
  };

  const handleViewScrapbook = () => {
    if (!selectedTrip) return;

    // Replay trips open the interactive timeline demo instead of the scrapbook.
    if (selectedTrip.isReplayTrip) {
      navigate('/replay');
      return;
    }

    // Map the sample trip IDs to the generated trip IDs used in TripScrapbook
    let targetTripId = 'trip-1';
    if (selectedTrip.id === 1) targetTripId = 'trip-1';
    if (selectedTrip.id === 2) targetTripId = 'trip-2';
    if (selectedTrip.id === 101) targetTripId = 'trip-3';
    if (selectedTrip.id === 102) targetTripId = 'trip-4';

    navigate('/trips', {
      state: {
        tripId: targetTripId
      }
    });
  };

  const closeScrapbook = () => {
    setShowScrapbook(false);
    setLightboxIndex(null);
  };

  // Lightbox Navigation
  const nextPhoto = useCallback(() => {
    if (selectedTrip && lightboxIndex !== null) {
      setLightboxIndex((prev) => (prev + 1) % selectedTrip.photos.length);
    }
  }, [selectedTrip, lightboxIndex]);

  const prevPhoto = useCallback(() => {
    if (selectedTrip && lightboxIndex !== null) {
      setLightboxIndex((prev) => (prev - 1 + selectedTrip.photos.length) % selectedTrip.photos.length);
    }
  }, [selectedTrip, lightboxIndex]);

  // Keyboard support for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') nextPhoto();
      if (e.key === 'ArrowLeft') prevPhoto();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, nextPhoto, prevPhoto]);

  const geoJSONData = {
    type: 'FeatureCollection',
    features: trips.map(t => createGeoJSONCircle(t.coordinates, 3))
  };

  return (
    <div className="app">
      {/* Map Container */}
      <div className="map-container">
        <Map
          {...viewState}
          onMove={evt => setViewState(evt.viewState)}
          style={{ width: '100%', height: '100%' }}
          mapStyle="https://tiles.openfreemap.org/styles/bright"
          attributionControl={false}
          onClick={handleMapClick}
        >
          <NavigationControl position="top-right" />

          {/* Current User Layer */}
          <Source id="user-data" type="geojson" data={geoJSONData}>
            <Layer
              id="user-layer"
              type="fill"
              paint={{
                'fill-color': users[viewingUserId].color,
                'fill-opacity': 0.3
              }}
            />
          </Source>

          {trips.map(trip => (
            <Marker
              key={trip.id}
              longitude={trip.coordinates[0]}
              latitude={trip.coordinates[1]}
              anchor="bottom"
              draggable={isEditMode}
              onDragEnd={(e) => {
                const updated = { ...trip, coordinates: [e.lngLat.lng, e.lngLat.lat] };
                setAllTrips(allTrips.map(t => t.id === trip.id ? updated : t));
                if (selectedTrip?.id === trip.id) {
                  setSelectedTrip(updated);
                }
              }}
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                handlePinClick(trip);
              }}
            >
              <div className="custom-marker" style={{ '--pin-color': users[trip.userId]?.color || '#FFD700' }}>
                {trip.userId !== 'user1' && (
                  <div className="marker-label" style={{ backgroundColor: users[trip.userId]?.color }}>
                    {users[trip.userId]?.name}
                  </div>
                )}
                <div className="pin-wrapper">
                  {trip.pinIcon ? (
                    <img
                      src={(trip.transparentBg && transparentBadges[trip.pinIcon]) || trip.pinIcon}
                      alt={trip.title}
                      className="pin-image"
                    />
                  ) : trip.icon ? (
                    <div className="pin-emoji" style={{ fontSize: '24px' }}>{trip.icon}</div>
                  ) : null}
                  {!trip.pinIcon && (
                    <div className="pin-tip" style={{ borderTopColor: users[trip.userId]?.color || '#FFD700' }} />
                  )}
                </div>
              </div>
            </Marker>
          ))}

          {selectedTrip && (
            <Popup
              longitude={selectedTrip.coordinates[0]}
              latitude={selectedTrip.coordinates[1]}
              anchor="bottom"
              offset={50}
              onClose={() => setSelectedTrip(null)}
              closeButton={true}
              closeOnClick={false}
              className="custom-popup"
            >
              <div className="popup-content">
                <div className="popup-header">
                  <h3>{selectedTrip.title}</h3>
                  <p style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#666', fontSize: '0.85rem' }}>
                    <MapPin size={14} /> {selectedTrip.city}
                  </p>
                </div>
                <p style={{ fontSize: '0.9rem', color: '#444', lineHeight: '1.4' }}>
                  {selectedTrip.description.substring(0, 80)}...
                </p>
                <button className="popup-btn" onClick={handleViewScrapbook}>
                  Open Scrapbook
                </button>
              </div>
            </Popup>
          )}
        </Map>
      </div>

      <div className="controls-container">
        <button className="reset-zoom-btn" onClick={resetZoom} title="Reset Map">
          <MapIcon size={20} />
          <span>US View</span>
        </button>
        <button
          className="reset-zoom-btn"
          onClick={() => navigate('/replay')}
          title="Trip Replay Demo"
          style={{ background: '#1d6bff', color: '#fff' }}
        >
          <Play size={20} />
          <span>Trip Replay</span>
        </button>
        <button
          className={`edit-mode-btn ${isEditMode ? 'active' : ''}`}
          onClick={() => setIsEditMode(!isEditMode)}
          title="Toggle Edit Mode"
        >
          {isEditMode ? <CheckCircle size={20} /> : <Camera size={20} />}
          <span>{isEditMode ? 'Done Editing' : 'Edit Mode'}</span>
        </button>
      </div>

      {/* Floating Sidebar */}
      <div className="sidebar-container">
        <motion.div
          className="sidebar-panel"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="sidebar-header">
            <div className="logo-area">
              <img src="/logo.jpg" alt="Logo" className="logo-img" />
              <div className="logo-text">
                <h1>Scrapost</h1>
                <p>Your Travel Memories</p>
              </div>
            </div>

            {/* User Profile Header */}
            <div className="user-profile-header" style={{ borderColor: users[viewingUserId].color }}>
              <div className="avatar-container">
                {users[viewingUserId].avatar ? (
                  <img src={users[viewingUserId].avatar} alt={users[viewingUserId].name} className="user-avatar" />
                ) : (
                  <div className="user-avatar-placeholder" style={{ backgroundColor: users[viewingUserId].color }}>
                    {users[viewingUserId].name[0]}
                  </div>
                )}
              </div>
              <div className="user-info">
                <h2>{users[viewingUserId].name}</h2>
                <p>{users[viewingUserId].username}</p>
              </div>
              {viewingUserId !== 'user1' && (
                <button className="back-home-btn" onClick={() => setViewingUserId('user1')}>
                  Back to Me
                </button>
              )}
            </div>

            {/* Tabs */}
            <div className="sidebar-tabs">
              <button 
                className={`tab-btn ${activeTab === 'trips' ? 'active' : ''}`}
                onClick={() => setActiveTab('trips')}
                style={{ '--active-color': users[viewingUserId].color }}
              >
                <MapIcon size={16} /> Trips
              </button>
              <button 
                className={`tab-btn ${activeTab === 'friends' ? 'active' : ''}`}
                onClick={() => setActiveTab('friends')}
                style={{ '--active-color': users[viewingUserId].color }}
              >
                <Users size={16} /> Friends
              </button>
            </div>
          </div>

          <div className="sidebar-content-scroll">
            {activeTab === 'trips' ? (
              <div className="trips-list">
                {trips.length === 0 ? (
                  <div className="empty-state">
                    <p>No trips yet.</p>
                  </div>
                ) : (
                  trips.map(trip => (
                    <div
                      key={trip.id}
                      className={`trip-card ${selectedTrip?.id === trip.id ? 'active' : ''}`}
                      onClick={() => {
                        if (trip.isReplayTrip) {
                          navigate('/replay');
                          return;
                        }
                        // Map trips to the data file
                        let targetTripId = 'trip-1';
                        if (trip.id === 1) targetTripId = 'trip-1';
                        if (trip.id === 2) targetTripId = 'trip-2';
                        if (trip.id === 101) targetTripId = 'trip-3';
                        if (trip.id === 102) targetTripId = 'trip-4';

                        navigate('/trips', {
                          state: {
                            tripId: targetTripId,
                            description: trip.description,
                            highlights: trip.highlights
                          }
                        });
                      }}
                      style={{ borderLeft: `4px solid ${users[trip.userId]?.color || '#ccc'}` }}
                    >
                      <div className="trip-icon">
                        {trip.icon ? (
                          <span style={{ fontSize: '1.5rem' }}>{trip.icon}</span>
                        ) : trip.pinIcon ? (
                          <img
                            src={(trip.transparentBg && transparentBadges[trip.pinIcon]) || trip.pinIcon}
                            alt={trip.title}
                            className="trip-card-icon"
                          />
                        ) : (
                          <MapIcon size={24} />
                        )}
                      </div>
                      <div className="trip-info">
                        <h3>{trip.title}</h3>
                        <p style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar size={12} /> {trip.date}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="friends-list">
                {users[viewingUserId].friends.map(friendId => {
                  const friend = users[friendId];
                  return (
                    <div 
                      key={friend.id} 
                      className="friend-card"
                      onClick={() => {
                        setViewingUserId(friend.id);
                        setActiveTab('trips');
                        setSelectedTrip(null);
                        // Optional: Reset zoom or fly to friend's location
                      }}
                    >
                      <div className="friend-avatar" style={{ backgroundColor: friend.color }}>
                        {friend.name[0]}
                      </div>
                      <div className="friend-info">
                        <h3>{friend.name}</h3>
                        <p>{friend.username}</p>
                      </div>
                      <ChevronRight size={16} color="#ccc" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Floating Action Button */}
      <motion.button
        className="add-trip-fab"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => {
          setIsEditMode(true);
          const newTrip = {
            id: Date.now(),
            userId: viewingUserId,
            title: 'New Adventure',
            city: 'New Location',
            coordinates: [viewState.longitude, viewState.latitude],
            date: 'Today',
            photos: [],
            icon: '📍',
            description: "Describe your adventure here...",
            highlights: []
          };
          setAllTrips([...allTrips, newTrip]);
          setSelectedTrip(newTrip);
          setShowScrapbook(true);
        }}
      >
        <Plus size={32} />
      </motion.button>

      {/* Scrapbook Overlay */}
      <AnimatePresence>
        {showScrapbook && selectedTrip && (
          <motion.div
            className="scrapbook-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="scrapbook-container"
              initial={{ y: 50, opacity: 0, scale: 0.9 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 50, opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <button className="close-btn-absolute" onClick={closeScrapbook}>
                <X size={24} />
              </button>

              <div className="scrapbook-scroll">
                {isEditMode && (
                  <div className="edit-form" style={{ marginBottom: '1rem', border: '2px dashed #4CAF50', background: '#e8f5e9' }}>
                    <h3 style={{ color: '#2e7d32', marginBottom: '0.5rem', fontSize: '1rem' }}>✏️ Edit Trip</h3>
                    <div className="form-group">
                      <label>Title</label>
                      <input value={selectedTrip.title} onChange={(e) => {
                        const updated = { ...selectedTrip, title: e.target.value };
                        setSelectedTrip(updated);
                        setAllTrips(allTrips.map(t => t.id === updated.id ? updated : t));
                      }} />
                    </div>
                  </div>
                )}

                <div className="scrapbook-content-grid">
                  {/* Column 1: Info */}
                  <div className="col-info">
                    <div className="panel-header">
                      <div className="trip-icon-large">{selectedTrip.icon || <MapIcon />}</div>
                      <div>
                        <h1>{selectedTrip.city}</h1>
                        <p className="trip-date">{selectedTrip.date}</p>
                      </div>
                    </div>
                    <div className="trip-description">
                      "{selectedTrip.description}"
                    </div>
                  </div>

                  {/* Column 2: Highlights */}
                  <div className="col-highlights">
                    <h3>Highlights</h3>
                    <div className="highlights-list">
                      {selectedTrip.highlights.map((highlight, index) => (
                        <div key={index} className="highlight-item-compact">
                          <CheckCircle size={16} className="check-icon" />
                          <span>{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Column 3: Photos */}
                  <div className="col-photos">
                    <div className="polaroid-strip">
                      {selectedTrip.photos.map((photo, index) => (
                        <div
                          key={index}
                          className="polaroid-compact"
                          style={{ '--rotation': `${Math.random() * 4 - 2}deg` }}
                          onClick={() => setLightboxIndex(index)}
                        >
                          <div className="tape-small"></div>
                          <img src={photo.url} alt={photo.caption || "Memory"} />
                        </div>
                      ))}
                      {selectedTrip.photos.length === 0 && (
                        <div className="polaroid-compact" style={{ '--rotation': '1deg' }}>
                          <div className="tape-small"></div>
                          <div className="no-photos-placeholder">No Photos</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox Overlay */}
      <AnimatePresence>
        {lightboxIndex !== null && selectedTrip && (
          <motion.div
            className="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxIndex(null)}
          >
            <button className="lightbox-close" onClick={() => setLightboxIndex(null)}>
              <X size={32} />
            </button>

            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              {selectedTrip.photos.length > 1 && (
                <button className="lightbox-nav-btn prev" onClick={prevPhoto}>
                  <ChevronLeft size={48} />
                </button>
              )}

              <div className="lightbox-image-container">
                <motion.img
                  key={lightboxIndex}
                  src={selectedTrip.photos[lightboxIndex].url}
                  alt="Full view"
                  className="lightbox-img"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                />
                {selectedTrip.photos[lightboxIndex].caption && (
                  <div className="lightbox-caption">
                    {selectedTrip.photos[lightboxIndex].caption}
                  </div>
                )}
              </div>

              {selectedTrip.photos.length > 1 && (
                <button className="lightbox-nav-btn next" onClick={nextPhoto}>
                  <ChevronRight size={48} />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/trips" element={<TripScrapbook />} />
        <Route path="/replay" element={<TripReplayDemo />} />
      </Routes>
    </Router>
  );
}

export default App;
